/**
 * DataForSEOConfigCard — inline form on /marketing-apis to save DataForSEO
 * Basic-Auth credentials. Validates against /v3/appendix/user_data on the
 * server, persists to db.integration_secrets, surfaces account balance + a
 * one-click delete to disconnect.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Check, X, KeyRound, Eye, EyeOff, Trash2, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COPY = {
  pt: {
    title: "Conectar DataForSEO",
    sub: "Plugue uma única conta DataForSEO para alimentar SEO Audit + Google Ads Keywords com dados reais.",
    login_lbl: "API Login", login_ph: "seu_login_da_api",
    pass_lbl: "API Password", pass_ph: "API password (não é a senha do dashboard)",
    save: "Validar & Salvar", saving: "Validando…",
    connected: "Conectado", balance: "Saldo",
    disconnect: "Desconectar", confirm_disconnect: "Tem certeza que quer remover as credenciais?",
    help: "Encontre sua API Login + Password em",
    help_link: "app.dataforseo.com → API Access",
    saved_ok: "DataForSEO conectado!", saved_fail: "Não consegui validar. Confira login/password.",
    deleted_ok: "Credenciais removidas.",
  },
  en: {
    title: "Connect DataForSEO",
    sub: "Plug a single DataForSEO account to power SEO Audit + Google Ads Keywords with real data.",
    login_lbl: "API Login", login_ph: "your_api_login",
    pass_lbl: "API Password", pass_ph: "API password (not your dashboard login)",
    save: "Validate & Save", saving: "Validating…",
    connected: "Connected", balance: "Balance",
    disconnect: "Disconnect", confirm_disconnect: "Are you sure you want to remove the credentials?",
    help: "Find your API Login + Password at",
    help_link: "app.dataforseo.com → API Access",
    saved_ok: "DataForSEO connected!", saved_fail: "Couldn't validate. Check login/password.",
    deleted_ok: "Credentials removed.",
  },
  es: {
    title: "Conectar DataForSEO",
    sub: "Conecta una sola cuenta DataForSEO para alimentar Auditoría SEO + Keywords de Google Ads con datos reales.",
    login_lbl: "API Login", login_ph: "tu_login_de_api",
    pass_lbl: "API Password", pass_ph: "API password (no la del dashboard)",
    save: "Validar y Guardar", saving: "Validando…",
    connected: "Conectado", balance: "Saldo",
    disconnect: "Desconectar", confirm_disconnect: "¿Seguro que quieres remover las credenciales?",
    help: "Encuentra tu API Login + Password en",
    help_link: "app.dataforseo.com → API Access",
    saved_ok: "¡DataForSEO conectado!", saved_fail: "No pude validar. Revisa login/password.",
    deleted_ok: "Credenciales eliminadas.",
  },
};

export default function DataForSEOConfigCard({ lang = "en", connected = false, onChanged }) {
  const t = COPY[lang] || COPY.en;
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);
  const [info, setInfo] = useState(null); // { balance_usd, currency } when last validated

  useEffect(() => {
    if (!connected) setInfo(null);
  }, [connected]);

  const save = async () => {
    if (!login.trim() || !password.trim()) return;
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/marketing-apis/dataforseo/credentials`, {
        login: login.trim(), password: password.trim(),
      });
      setInfo({ balance_usd: data.balance_usd, currency: data.currency || "USD" });
      toast.success(t.saved_ok);
      // Wipe password field for safety; keep login visible.
      setPassword("");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t.saved_fail);
    } finally { setSaving(false); }
  };

  const disconnect = async () => {
    if (!window.confirm(t.confirm_disconnect)) return;
    try {
      await axios.delete(`${API}/marketing-apis/dataforseo/credentials`);
      setLogin(""); setPassword(""); setInfo(null);
      toast.success(t.deleted_ok);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div data-testid="dataforseo-config-card" className="al-card p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-[#3B82F6]" />
            <h3 className="text-[14px] font-bold tracking-tight">{t.title}</h3>
            {connected && (
              <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 border border-emerald-200 font-bold">
                <Check className="w-3 h-3" /> {t.connected}
              </span>
            )}
          </div>
          <p className="text-[12px] text-[var(--al-text-muted)] mt-1.5 max-w-xl">{t.sub}</p>
        </div>
        {connected && (
          <button
            type="button"
            data-testid="dataforseo-disconnect"
            onClick={disconnect}
            title={t.disconnect}
            className="shrink-0 inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 text-[11.5px] font-semibold"
          >
            <Trash2 className="w-3.5 h-3.5" /> {t.disconnect}
          </button>
        )}
      </div>

      <div className="grid sm:grid-cols-2 gap-3 mt-4">
        <label>
          <div className="text-[10.5px] uppercase tracking-wide text-[var(--al-text-muted)] font-semibold mb-1.5">{t.login_lbl}</div>
          <input
            type="text"
            value={login}
            onChange={(e) => setLogin(e.target.value)}
            placeholder={t.login_ph}
            autoComplete="off"
            data-testid="dataforseo-login-input"
            className="w-full al-input h-9 text-[12.5px]"
          />
        </label>
        <label>
          <div className="text-[10.5px] uppercase tracking-wide text-[var(--al-text-muted)] font-semibold mb-1.5">{t.pass_lbl}</div>
          <div className="relative">
            <input
              type={showPw ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t.pass_ph}
              autoComplete="new-password"
              data-testid="dataforseo-password-input"
              className="w-full al-input h-9 text-[12.5px] pr-9"
            />
            <button
              type="button"
              onClick={() => setShowPw((v) => !v)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--al-text-muted)] hover:text-[var(--al-text)]"
              data-testid="dataforseo-password-toggle"
              title={showPw ? "Hide" : "Show"}
            >
              {showPw ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
            </button>
          </div>
        </label>
      </div>

      <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="text-[11px] text-[var(--al-text-muted)]">
          {t.help}{" "}
          <a
            href="https://app.dataforseo.com/api-access"
            target="_blank" rel="noopener noreferrer"
            className="text-[#3B82F6] hover:underline inline-flex items-center gap-0.5"
            data-testid="dataforseo-help-link"
          >
            {t.help_link} <ExternalLink className="w-3 h-3" />
          </a>
        </div>
        <div className="flex items-center gap-3">
          {info && info.balance_usd !== undefined && (
            <div className="text-[11.5px] font-semibold text-emerald-700" data-testid="dataforseo-balance">
              {t.balance}: <strong>${(info.balance_usd ?? 0).toFixed(2)} {info.currency}</strong>
            </div>
          )}
          <button
            type="button"
            data-testid="dataforseo-save-btn"
            onClick={save}
            disabled={saving || !login.trim() || !password.trim()}
            className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-[#3B82F6] hover:bg-[#2563EB] text-white text-[12.5px] font-bold disabled:opacity-60"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
            {saving ? t.saving : t.save}
          </button>
        </div>
      </div>
    </div>
  );
}
